// src/features/tidsregistering/components/attendance/AttendanceRow.jsx
import React, { memo } from 'react';
import { Box, alpha, useTheme } from '@mui/material';
import TimeBlockBar from './TimeBlockBar';
import PlannedShiftBar from './PlannedShiftBar';
import { useDragCreateHour } from './useDragCreateHour';

// Two tracks: top (plan) 40px + 8px gap + bottom (actual) 40px = 88px
const TRACK_HEIGHT = 40;
const TRACK_GAP = 8;
export const ROW_HEIGHT = TRACK_HEIGHT * 2 + TRACK_GAP;
const GHOST_VERTICAL_INSET = 4;

/**
 * Renders one staff-member row for the attendance chart with two tracks:
 *   Top track (0–40px):    PlannedShift bars — drag creates a planned shift
 *   Bottom track (48–88px): TimeDetail bars  — drag creates a registration
 *
 * Props:
 *   category           — Category from attendanceAdapter
 *   timeToX            — (Date) => px from useHourViewLogic
 *   visibleDayIndices  — visible slot indices, injected by renderRows
 *   dayWidth           — slot width px, injected by renderRows
 *   slotIndexToTime    — (idx) => Date, injected via renderRows closure
 *   onSlotClick        — (slot) => void
 *   onDragCreate       — ({ category, startTime, endTime }) => void | null  (registration track)
 *   onShiftDragCreate  — ({ category, startTime, endTime }) => void | null  (shift track)
 */
function AttendanceRow({
  category,
  timeToX,
  visibleDayIndices,
  dayWidth,
  slotIndexToTime,
  onSlotClick,
  onDragCreate,
  onShiftDragCreate,
  unavailabilityBlocks = [],
  selectedDate = '',
}) {
  const theme = useTheme();
  const { shiftSlots = [], detailSlots = [] } = category;

  const HATCH = 'repeating-linear-gradient(-45deg, rgba(0,0,0,0.14) 0px, rgba(0,0,0,0.14) 2px, rgba(0,0,0,0.04) 2px, rgba(0,0,0,0.04) 9px)';

  const unavailabilityOverlays = unavailabilityBlocks.map((block, i) => {
    if (block.is_full_day || !block.start_time || !block.end_time) {
      return { key: `unavail-${i}`, left: 0, width: '100%', isFull: true };
    }
    const startDt = new Date(`${selectedDate}T${block.start_time}`);
    const endDt = new Date(`${selectedDate}T${block.end_time}`);
    const leftPx = timeToX(startDt);
    const widthPx = timeToX(endDt) - leftPx;
    if (widthPx <= 0) return null;
    return { key: `unavail-${i}`, left: leftPx, width: widthPx, isFull: false };
  }).filter(Boolean);

  // Drag hook for the bottom track (registrations)
  const {
    rowRef: regRowRef,
    isDragging: isRegDragging,
    ghostBar: regGhostBar,
    handleMouseDown: handleRegMouseDown,
  } = useDragCreateHour({
    visibleSlotIndices: visibleDayIndices,
    slotWidthPx: dayWidth,
    slotIndexToTime,
    onDragCreate: onDragCreate
      ? ({ startTime, endTime }) => onDragCreate({ category, startTime, endTime })
      : null,
  });

  // Drag hook for the top track (planned shifts)
  const {
    rowRef: shiftRowRef,
    isDragging: isShiftDragging,
    ghostBar: shiftGhostBar,
    handleMouseDown: handleShiftMouseDown,
  } = useDragCreateHour({
    visibleSlotIndices: visibleDayIndices,
    slotWidthPx: dayWidth,
    slotIndexToTime,
    onDragCreate: onShiftDragCreate
      ? ({ startTime, endTime }) => onShiftDragCreate({ category, startTime, endTime })
      : null,
  });

  return (
    <Box
      sx={{
        position: 'relative',
        height: ROW_HEIGHT,
        borderBottom: `1px solid ${theme.palette.divider}`,
        bgcolor: 'background.paper',
      }}
    >
      {/* ── Top track: Planned shifts ── */}
      <Box
        ref={shiftRowRef}
        onMouseDown={handleShiftMouseDown}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: TRACK_HEIGHT,
          bgcolor: alpha(theme.palette.primary.main, 0.04),
          borderBottom: `1px dashed ${theme.palette.divider}`,
          cursor: isShiftDragging ? 'col-resize' : (onShiftDragCreate ? 'crosshair' : 'default'),
          userSelect: 'none',
          overflow: 'hidden',
        }}
      >
        {unavailabilityOverlays.map(({ key, left, width, isFull }) => (
          <Box
            key={key}
            sx={{
              position: 'absolute',
              left: isFull ? 0 : left,
              top: 0,
              width: isFull ? '100%' : width,
              height: '100%',
              background: HATCH,
              pointerEvents: 'none',
              zIndex: 0,
            }}
          />
        ))}

        {shiftSlots.map((slot) => (
          <PlannedShiftBar
            key={slot.id}
            slot={slot}
            timeToX={timeToX}
            trackHeight={TRACK_HEIGHT}
          />
        ))}

        {shiftGhostBar && (
          <Box
            sx={{
              position: 'absolute',
              left: shiftGhostBar.leftPx,
              top: GHOST_VERTICAL_INSET,
              width: Math.max(shiftGhostBar.widthPx, 2),
              height: TRACK_HEIGHT - GHOST_VERTICAL_INSET * 2,
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              border: `1.5px dashed ${theme.palette.primary.main}`,
              borderRadius: 0.5,
              pointerEvents: 'none',
              zIndex: 2,
            }}
          />
        )}
      </Box>

      {/* ── Bottom track: Actual registrations ── */}
      <Box
        ref={regRowRef}
        onMouseDown={handleRegMouseDown}
        sx={{
          position: 'absolute',
          top: TRACK_HEIGHT + TRACK_GAP,
          left: 0,
          right: 0,
          height: TRACK_HEIGHT,
          cursor: isRegDragging ? 'col-resize' : (onDragCreate ? 'crosshair' : 'default'),
          userSelect: 'none',
          overflow: 'hidden',
        }}
      >
        {unavailabilityOverlays.map(({ key, left, width, isFull }) => (
          <Box
            key={key}
            sx={{
              position: 'absolute',
              left: isFull ? 0 : left,
              top: 0,
              width: isFull ? '100%' : width,
              height: '100%',
              background: HATCH,
              pointerEvents: 'none',
              zIndex: 0,
            }}
          />
        ))}

        {detailSlots
          .sort((a, b) => (b.endDate - b.startDate) - (a.endDate - a.startDate))
          .map((slot) => (
            <TimeBlockBar
              key={slot.id}
              slot={slot}
              timeToX={timeToX}
              rowHeight={TRACK_HEIGHT}
              onClick={onSlotClick
                ? (s) => onSlotClick({ ...s, staffMember: category.staffMember })
                : undefined}
            />
          ))}

        {regGhostBar && (
          <Box
            sx={{
              position: 'absolute',
              left: regGhostBar.leftPx,
              top: GHOST_VERTICAL_INSET,
              width: Math.max(regGhostBar.widthPx, 2),
              height: TRACK_HEIGHT - GHOST_VERTICAL_INSET * 2,
              bgcolor: alpha(theme.palette.success.main, 0.15),
              border: `1.5px dashed ${theme.palette.success.main}`,
              borderRadius: 0.5,
              pointerEvents: 'none',
              zIndex: 2,
            }}
          />
        )}
      </Box>
    </Box>
  );
}

export const ATTENDANCE_ROW_HEIGHT = ROW_HEIGHT;
export default memo(AttendanceRow);
